import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  InjectionToken,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutoComplete } from 'primeng/autocomplete';
import { Observable } from 'rxjs';
import { RbacService } from '../../../common-api/system-management-api';
import { isNullOrEmpty } from '../../../core/utils/common-functions';
import { genCommonObserverObj } from '../../../core/utils/ng-functions';
import { BaseComponent } from '../base.component';

export const STAFF_API_SOURCE_TOKEN = new InjectionToken<
  Observable<StaffSearchViewModel>
>('staff api source');

export const STAFF_SELECTOR_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => StaffSelectorComponent),
  multi: true,
};

/**
 * 員工下拉選單元件
 *
 * @export
 * @class StaffSelectorComponent
 * @extends {BaseComponent}
 * @implements {OnInit}
 * @implements {OnChanges}
 * @implements {AfterViewInit}
 * @implements {ControlValueAccessor}
 */
@Component({
  selector: 'sigv-staff-selector',
  templateUrl: './staff-selector.component.html',
  styleUrls: ['./staff-selector.component.scss'],
  providers: [STAFF_SELECTOR_VALUE_ACCESSOR],
})
export class StaffSelectorComponent
  extends BaseComponent
  implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor
{
  /**
   * 移動元件至
   */
  @Input() appendTo: any;

  /**
   * 唯讀
   */
  @Input() readonly: any;

  /**
   * 下拉框樣式
   */
  @Input() panelStyleClass: any;

  /**
   * FormControllName
   */
  @Input() formControlName?: string;

  /**
   * 是否取得所有員工
   *
   * @memberof StaffSelectorComponent
   */
  @Input() getAll = true;

  @Output() blur = new EventEmitter<any>();

  /**
   * Primeng AutoComplete 元件
   */
  @ViewChild(AutoComplete) autoCompleteComponent!: AutoComplete;
  model?: string;
  staffInfos: StaffDisplayInfo[] = [];
  filteredStaffInfos: StaffDisplayInfo[] = [];
  placeholder: any;
  onModelChange = (event: string | undefined) => {};
  onModelTouched = () => {};

  constructor(
    private injector: Injector,
    private cd: ChangeDetectorRef,
    private rbacService: RbacService,
    @Optional()
    @Inject(STAFF_API_SOURCE_TOKEN)
    private apiSource: Observable<StaffSearchViewModel[]>
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {}

  ngOnInit(): void {
    this.setPlaceholder();
    this.translateService.onLangChange.subscribe({
      next: (event: any) => {
        this.setPlaceholder();
        this.cd.detectChanges();
      },
    });

    const searchParams = {
      filters: {},
      orderSettings: [],
      currentPage: 0,
      pageSize: 99999,
    };

    const apiSourceObj = this.getAll
      ? this.rbacService.rbacUsableUserGet()
      : this.apiSource;

    // 取得人員列表
    apiSourceObj.subscribe(
      genCommonObserverObj({
        next: (results: StaffSearchViewModel[]) => {
          this.filteredStaffInfos = this.staffInfos = results.map((x) => ({
            userNo: x.userNo,
            userName: x.userName,
            displayText: `${x.userNo} ${x.userName}`,
          }));

          // 如果一開始有 binding 資料就要去帶出 staffinfo
          if (isNullOrEmpty(this.model) === false) {
            const targetStaffInfo = this.staffInfos.find(
              (x) => x.userNo === this.model
            );
            if (isNullOrEmpty(targetStaffInfo) === false) {
              this.updateSelectorValue(targetStaffInfo);
            }
          }
        },
      })
    );
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  writeValue(obj: any): void {
    this.model = obj;
    // 如果一開始有 binding 資料就要去帶出 staffinfo
    if (isNullOrEmpty(this.model) === false) {
      const targetStaffInfo = this.staffInfos.find(
        (x) => x.userNo === this.model
      );
      if (isNullOrEmpty(targetStaffInfo) === false) {
        this.updateSelectorValue(targetStaffInfo);
      } else {
        this.updateSelectorValue(undefined);
      }
    } else {
      this.updateSelectorValue(undefined);
    }
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onModelTouched = fn;
  }

  /**
   * 選項 select 事件
   *
   * @param {*} event
   * @memberof StaffSelectorComponent
   */
  onAutoCompleteSelect(event: any): void {
    this.onModelChange(event?.userNo);
  }

  /**
   * blur 事件
   *
   * @param {*} event
   * @memberof StaffSelectorComponent
   */
  onAutoCompleteBlur(event: any): void {
    this.checkData(event);
    this.blur.emit(event);
  }

  /**
   * 過濾資料並顯示符合選項
   *
   * @param {*} event
   * @memberof StaffSelectorComponent
   */
  filterData(event: any): void {
    const queryStr = event.query as string;
    this.filteredStaffInfos = this.staffInfos.filter((x) =>
      x.displayText?.toUpperCase().includes(queryStr.toUpperCase())
    );
    const targetStaffInfo = this.filteredStaffInfos.find(
      (x) => x.displayText === queryStr
    );
    if (isNullOrEmpty(targetStaffInfo) === false) {
      this.onModelChange(targetStaffInfo?.userNo);
      this.updateSelectorValue(targetStaffInfo);
    }
  }

  /**
   * 檢查是否為合法的值
   *
   * @param {*} event
   * @memberof StaffSelectorComponent
   */
  checkData(event: any): void {
    const targetStaffInfo = this.filteredStaffInfos.find(
      (x) => x.displayText === event.target.value
    );
    if (isNullOrEmpty(targetStaffInfo) === false) {
      this.onModelChange(targetStaffInfo?.userNo);
      this.updateSelectorValue(targetStaffInfo);
    } else {
      this.onModelChange(undefined);
      this.updateSelectorValue();
    }
  }

  /**
   * 更新 primeng autocomplte 值
   *
   * @private
   * @param {StaffDisplayInfo} [value]
   * @memberof StaffSelectorComponent
   */
  private updateSelectorValue(value?: StaffDisplayInfo): void {
    if (isNullOrEmpty(this.autoCompleteComponent) === false) {
      this.autoCompleteComponent.writeValue(value);
    }
  }

  private setPlaceholder(): void {
  }
}

export interface StaffDisplayInfo {
  userNo?: string;
  userName?: string;
  displayText?: string;
}

export interface StaffSearchViewModel {
  userNo?: string;
  userName?: string;
}
